/**
 * Query key factory for MoneyMitra Weekly.
 *
 * Three reads, three shapes of key, and the split is the whole point.
 *
 * The archive keys on the PAGE NUMBER. It is offset-paged, not a cursor feed,
 * so page 2 is a different answer from page 1 rather than more of it — a key
 * that dropped the number would serve page 1's twelve cards under page 2 the
 * moment the pager moved. Page size rides in the key too: it is part of what
 * was asked for, and two components reading the same archive at different
 * sizes must not share one cache entry.
 *
 * An issue keys on its ISSUE NUMBER, which is also the route key. Not the
 * slug: `slug` is on the payload but no route accepts it, so keying on it
 * would invent an identifier the cache and the URL disagree about.
 *
 * `latest` is its own key and NOT `issue(n)` for whatever n it resolves to.
 * The two are genuinely different questions — "the newest issue, whichever
 * that is" versus "issue 20" — and they stop agreeing the instant a new issue
 * publishes. Aliasing them would mean the hero card and the reader page share
 * a cache entry that only one of them can be right about. The price is that
 * the newest issue is cached twice when a member opens it from the hero;
 * correct, just not shared.
 */
export const newsletterKeys = {
  all: ['newsletter'] as const,

  /** The branch to invalidate to drop every archive page at once. */
  archives: () => [...newsletterKeys.all, 'archive'] as const,

  archive: (page: number, pageSize: number) =>
    [...newsletterKeys.archives(), page, pageSize] as const,

  /** The branch for the reader pages. */
  issues: () => [...newsletterKeys.all, 'issue'] as const,

  issue: (issueNumber: number) => [...newsletterKeys.issues(), issueNumber] as const,

  /** Deliberately a sibling of `issues()`, not a member — see the note above. */
  latest: () => [...newsletterKeys.all, 'latest'] as const,

  /** The signed-in member's "Get new issues" switch. */
  subscription: () => [...newsletterKeys.all, 'subscription'] as const,
}
